<script setup lang="ts">
import { onClickOutside, useWindowSize } from "@vueuse/core";
import { nanoid } from "nanoid";
import InputWrapper from "@/stories/form/InputWrapper/InputWrapper.vue";
import { onMounted, ref, toRef, watch, onBeforeUnmount, toRefs } from "vue";
import { useFieldValidationError } from "@/composables/useFieldValidationError";
import type { DefaultInputProps, IOption } from "@/interfaces";
import DropdownTrigger from "@/stories/buttons/DropdownTrigger/DropdownTrigger.vue";
import SelectItem from "@/stories/form/SelectInput/SelectItem.vue";
import type { PrimitiveModel } from "@/types";
import Line from "@/stories/layout/Line/Line.vue";
import ButtonLink from "@/stories/buttons/ButtonLink/ButtonLink.vue";
import ChooseFromExistingList from "@/stories/buttons/Presets/ChooseFromExistingList.vue";
import SVG from "@/stories/displays/SVG/SVG.vue";
import type { SVGName } from "@/SVGs";

const { width } = useWindowSize();
const breakpoint = width.value < 1025 ? "mobile" : "desktop";

const id = nanoid();
const isListOpen = ref(false);
const buttonRef = ref();
const listRef = ref();
const resizeObserver = ref();

interface Props extends Omit<DefaultInputProps, "label"> {
  label?: string;
  modelValue?: PrimitiveModel;
  items: IOption[];
  fullWidth?: boolean;
  padding?: string;
  icon?: SVGName;
  size?: string;
  autoDeselect?: boolean;
  showCreateNewButton?: boolean;
  showCreateButtonDescription?: boolean;
  createNewButtonLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  fullWidth: true,
  padding: "px-4 py-2 h-[2.5rem]",
  autoDeselect: true,
  showCreateNewButton: false,
  showCreateButtonDescription: false,
});

const emit = defineEmits<{
  "update:modelValue": [PrimitiveModel];
  createNew: [];
}>();

const { validation, name, autoDeselect, isRequired } = toRefs(props);
const internalModel = ref<PrimitiveModel>(undefined);
const internalModelViaProps = toRef(props, "modelValue"); // modelValue passed from parents, not meant to be mutated.
const fieldRef = ref();
const fieldWidth = ref(0);

watch(internalModelViaProps, (value: PrimitiveModel) => {
  internalModel.value = value;
});

onMounted(() => {
  internalModel.value = props.modelValue;

  if (fieldRef.value) {
    const element = fieldRef.value;

    resizeObserver.value = new ResizeObserver(function () {
      fieldWidth.value = element.clientWidth;
    });

    fieldWidth.value = element.clientWidth;
    resizeObserver.value.observe(element);
  }
});

onBeforeUnmount(() => {
  if (fieldRef.value && resizeObserver.value) {
    const element = fieldRef.value;
    resizeObserver.value.unobserve(element);
  }
});

const closeList = () => {
  internalModel.value = props.modelValue;
  isListOpen.value = false;
};

const handleUpdate = () => {
  if (props.modelValue === internalModel.value && autoDeselect.value) {
    handleInput(undefined);
  } else {
    handleInput(internalModel.value);
  }
};

// Set the chosen value (internalModel.value) to the value of the selected item.
// If the selected item was already chosen, set it to undefined to deselect.
const handleInternalClick = (item: IOption) => {
  internalModel.value = item.value;
  handleUpdate();
};

if (breakpoint === "desktop") {
  onClickOutside(listRef, closeList, { ignore: [buttonRef] });
}

const handleInput = (value: PrimitiveModel) => {
  emit("update:modelValue", value);

  if (validation.value && validation.value.errors) {
    delete validation.value.errors[name.value];
  }

  closeList();
};

const { hasError } = useFieldValidationError(name, validation);
</script>

<template>
  <DropdownTrigger
    :is-mobile-drawer="breakpoint === 'mobile'"
    :full-width="true"
    :label="label"
    :widthStrategy="'full'"
    :saveButtonDisplayment="'hide'"
    :disabled="disabled"
    @close="closeList"
  >
    <template #trigger="{ isActive }">
      <InputWrapper
        :label="label"
        :forId="id"
        :isRequired="isRequired"
        :inputKey="inputKey"
        :tooltip="tooltip"
        :disabled="disabled"
        :description="description"
        :name="name"
        :validation="validation"
        :errorMessageKey="errorMessageKey"
      >
        <button
          type="button"
          ref="buttonRef"
          role="listbox"
          class="flex justify-between items-center dropdown primary rounded-lg transition text-left overflow-hidden"
          :class="{
            'text-neutral600': disabled,
            'hover:border-neutral500': !disabled,
            active: isActive,
            error: hasError,
            [`dropdown-${id}`]: true,
            'pl-12': icon,
            'w-full': fullWidth,
            [padding]: true,
          }"
          :disabled="disabled"
          @click="isListOpen = !isListOpen"
        >
          <div
            v-if="modelValue !== undefined"
            class="b300"
            :class="{ 'text-neutral600': disabled }"
          >
            {{ items.find((i) => i.value === modelValue)?.title }}
          </div>
          <div
            v-else
            class="b300"
            :class="[disabled ? 'text-neutral500' : 'text-neutral1000']"
          >
            {{ placeholder || $t("global.select") }}
          </div>
          <SVG
            icon="fas-chevron-down"
            height="1.25rem"
            width="1.25rem"
            class="transition-transform"
            :class="{ 'rotate-180': isListOpen }"
          />
          <SVG
            v-if="icon"
            :icon="icon"
            height="1.25rem"
            width="1.25rem"
            class="absolute left-5 text-neutral700"
          />
        </button>
      </InputWrapper>
    </template>

    <template #content>
      <div v-if="showCreateNewButton">
        <ButtonLink
          :text="createNewButtonLabel || ''"
          iconSVG="fas-circle-plus"
          class="w-full"
          @click="$emit('createNew')"
        />
        <Line v-if="items.length" class="my-2" custom />
      </div>
      <ChooseFromExistingList v-if="showCreateButtonDescription" />
      <SelectItem
        v-for="(item, index) of items"
        :key="index"
        @update="handleInternalClick"
        :model="internalModel"
        :item="item"
        :groupId="id"
      />
    </template>
  </DropdownTrigger>
</template>

<style lang="scss">
.dropdown {
  &.primary {
    background: theme("colors.neutral0");
    color: theme("colors.neutral1000");
    outline: 1px solid theme("colors.neutral500");

    &.error {
      outline: 2px solid theme("colors.red400");
    }

    &.active,
    &:focus,
    &:focus-visible {
      outline: 1px solid transparent;
      outline: 2px solid theme("colors.neutral1000");
    }

    &.disabled {
      color: theme("colors.neutral300");
      pointer-events: none;
    }
  }
}

.dropdown-item {
  & div.icon {
    transition: all 200ms;
    background-image: url("@/assets/svg/radio/default.svg");
    background-repeat: no-repeat;
    background-position: center center;
    height: theme("spacing.7");
    width: theme("spacing.7");
  }

  &:hover {
    & div.icon {
      background-image: url("@/assets/svg/radio/hover.svg");
    }
  }

  &:disabled {
    & div.icon {
      background-image: url("@/assets/svg/radio/disabled.svg");
    }
  }

  &.selected {
    & div.icon {
      background-image: url("@/assets/svg/radio/selected-default.svg");
    }

    &:hover {
      & div.icon {
        background-image: url("@/assets/svg/radio/selected-hover.svg");
      }
    }

    &:disabled {
      & div.icon {
        background-image: url("@/assets/svg/radio/selected-disabled.svg");
      }
    }
  }
}
</style>
